/**
 * Concurrency Limiter - Sprint 11e Fix 0C
 *
 * Limits concurrent async operations to prevent API rate limit errors.
 *
 * CRITICAL: Running too many model executions in parallel can trigger
 * rate limits from LLM providers. This limiter ensures we stay within
 * safe concurrency bounds.
 *
 * @see docs/sprints/ownyou-sprint11e-spec.md - Fix 0C
 */

/**
 * Default concurrent execution limit
 *
 * 2 is a safe default that works for most LLM providers:
 * - OpenAI: 60 RPM for free tier → 2 concurrent is safe
 * - Anthropic: 60 RPM for Claude → 2 concurrent is safe
 * - Groq: Higher limits, but 2 is conservative
 */
export const DEFAULT_CONCURRENCY_LIMIT = 2;

/**
 * Type for the limiter function returned by createConcurrencyLimiter
 */
export type ConcurrencyLimiter = <T>(fn: () => Promise<T>) => Promise<T>;

/**
 * Create a concurrency limiter function
 *
 * Implements a simple semaphore-style limiter without external dependencies.
 * This is equivalent to using p-limit(n) but avoids the npm dependency.
 *
 * @param limit - Maximum concurrent executions (default: 2)
 * @returns Limiter function that wraps async tasks
 *
 * @example
 * ```typescript
 * const limit = createConcurrencyLimiter(2);
 *
 * // Only 2 tasks run at a time
 * await Promise.all([
 *   limit(() => fetch('https://api1.com')),
 *   limit(() => fetch('https://api2.com')),
 *   limit(() => fetch('https://api3.com')), // Waits for one of the above
 * ]);
 * ```
 */
export function createConcurrencyLimiter(
  limit: number = DEFAULT_CONCURRENCY_LIMIT
): ConcurrencyLimiter {
  // Queue of pending tasks
  const queue: Array<() => void> = [];
  // Number of currently running tasks
  let running = 0;

  // Try to run the next task from the queue
  const runNext = (): void => {
    if (queue.length > 0 && running < limit) {
      running++;
      const next = queue.shift();
      next?.();
    }
  };

  // Return the limiter function
  return async <T>(fn: () => Promise<T>): Promise<T> => {
    return new Promise((resolve, reject) => {
      const task = async (): Promise<void> => {
        try {
          const result = await fn();
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          running--;
          runNext();
        }
      };

      if (running < limit) {
        running++;
        task();
      } else {
        queue.push(task);
      }
    });
  };
}

/**
 * Options for runWithConcurrencyLimit
 */
export interface ConcurrencyLimitOptions {
  /**
   * If true, include Error objects in results instead of throwing
   * Default: false
   */
  includeErrors?: boolean;
}

/**
 * Run an array of async tasks with concurrency limiting
 *
 * This is a convenience wrapper around createConcurrencyLimiter
 * for common use cases where you have an array of tasks.
 *
 * Results are returned in the same order as input tasks,
 * regardless of completion order.
 *
 * @param tasks - Array of async functions to execute
 * @param limit - Maximum concurrent executions (default: 2)
 * @param options - Options for error handling
 * @returns Array of results in same order as input tasks
 *
 * @example
 * ```typescript
 * const models = ['gpt-4o', 'claude-3', 'gemini-pro'];
 *
 * const results = await runWithConcurrencyLimit(
 *   models.map(model => () => classifyWithModel(model, emails)),
 *   2  // Only 2 models run at a time
 * );
 * ```
 */
export async function runWithConcurrencyLimit<T>(
  tasks: Array<() => Promise<T>>,
  limit: number = DEFAULT_CONCURRENCY_LIMIT,
  options: ConcurrencyLimitOptions = {}
): Promise<(T | Error)[]> {
  const { includeErrors = false } = options;

  if (tasks.length === 0) {
    return [];
  }

  const limiter = createConcurrencyLimiter(limit);

  // Wrap each task to preserve order and optionally handle errors
  const wrappedTasks = tasks.map(async (task, index) => {
    try {
      const result = await limiter(task);
      return { index, result, error: null };
    } catch (error) {
      return { index, result: null, error: error as Error };
    }
  });

  // Wait for all tasks
  const rawResults = await Promise.all(wrappedTasks);

  // Sort by original index
  rawResults.sort((a, b) => a.index - b.index);

  // Map to results, handling errors based on options
  return rawResults.map(({ result, error }) => {
    if (error) {
      if (includeErrors) {
        return error;
      }
      // If not including errors, return the result (which is null)
      // This maintains array length but with null values
      return error;
    }
    return result as T;
  });
}

/**
 * Semaphore class for more complex concurrency control
 *
 * Provides acquire/release pattern for cases where you need
 * more control over the concurrency lifecycle.
 */
export class Semaphore {
  private count: number;
  private readonly max: number;
  private readonly waiting: Array<() => void> = [];

  constructor(max: number = DEFAULT_CONCURRENCY_LIMIT) {
    this.max = max;
    this.count = 0;
  }

  /**
   * Acquire a semaphore slot
   * Resolves immediately if slots available, waits otherwise
   */
  async acquire(): Promise<void> {
    if (this.count < this.max) {
      this.count++;
      return;
    }

    return new Promise((resolve) => {
      this.waiting.push(() => {
        this.count++;
        resolve();
      });
    });
  }

  /**
   * Release a semaphore slot
   * Wakes up next waiting task if any
   */
  release(): void {
    this.count--;
    const next = this.waiting.shift();
    if (next) {
      next();
    }
  }

  /**
   * Run a function with automatic acquire/release
   */
  async run<T>(fn: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await fn();
    } finally {
      this.release();
    }
  }

  /**
   * Get current number of active slots
   */
  get active(): number {
    return this.count;
  }

  /**
   * Get number of waiting tasks
   */
  get pending(): number {
    return this.waiting.length;
  }
}
